import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { AppSidebar, SidebarMenuButton } from "@/pages/AppSidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Calculator, ArrowLeft, Printer, ChevronDown, ChevronUp,
  Heart, Scale, Droplets, Dumbbell, Flame, Activity,
  Target, Utensils, Syringe, Beaker
} from "lucide-react";

/* ================================================================
   PRINT HELPER — prints only the active calculator result area
   ================================================================ */
function printResult(id) {
  const el = document.getElementById(id);
  if (!el) return;
  const w = window.open("", "_blank");
  w.document.write(`<html><head><title>Kalkulaatori tulemus</title>
    <style>
      *{box-sizing:border-box;margin:0;padding:0}
      body{font-family:system-ui,-apple-system,sans-serif;padding:28px 32px;color:#1e293b;font-size:14px;line-height:1.5}
      h2{margin-bottom:12px;font-size:18px;font-weight:700}
      h3{margin:12px 0 8px;font-size:14px;font-weight:600;color:#334155}

      /* Grid system matching Tailwind */
      .grid{display:grid;gap:12px;margin:8px 0}
      .gap-3{gap:12px}
      .grid-cols-2{grid-template-columns:repeat(2,1fr)}
      .grid-cols-3{grid-template-columns:repeat(3,1fr)}
      .grid-cols-4{grid-template-columns:repeat(4,1fr)}
      .sm\\:grid-cols-4{grid-template-columns:repeat(4,1fr)}
      .sm\\:grid-cols-3{grid-template-columns:repeat(3,1fr)}

      /* Cards */
      .rounded-lg,.rounded-xl{border-radius:10px}
      .p-3{padding:10px}.p-4{padding:14px}
      .text-center{text-align:center}
      .text-xs{font-size:11px}.text-sm{font-size:13px}
      .text-xl{font-size:20px;font-weight:700;margin:4px 0}
      .text-2xl{font-size:24px;font-weight:700;margin:4px 0}
      .font-bold{font-weight:700}

      /* Background colors for cards */
      .bg-slate-50{background:#f8fafc;border:1px solid #e2e8f0}
      .bg-amber-50{background:#fffbeb;border:1px solid #fde68a}
      .bg-rose-50{background:#fff1f2;border:1px solid #fecdd3}
      .bg-blue-50{background:#eff6ff;border:1px solid #bfdbfe}
      .bg-green-50{background:#f0fdf4;border:1px solid #bbf7d0}
      .bg-orange-50{background:#fff7ed;border:1px solid #fed7aa}
      .bg-purple-50{background:#faf5ff;border:1px solid #e9d5ff}
      .bg-sky-50{background:#f0f9ff;border:1px solid #bae6fd}

      /* Text colors */
      .text-slate-500{color:#64748b}.text-slate-400{color:#94a3b8}
      .text-amber-600{color:#d97706}.text-rose-600{color:#e11d48}
      .text-blue-600{color:#2563eb}.text-green-600{color:#16a34a}
      .text-orange-600{color:#ea580c}.text-purple-600{color:#9333ea}
      .text-sky-600{color:#0284c7}.text-slate-700{color:#334155}

      /* Badge */
      .badge{display:inline-block;padding:2px 10px;border-radius:12px;font-size:12px;font-weight:600;margin-top:4px}
      .green{background:#dcfce7;color:#166534}.yellow{background:#fef9c3;color:#854d0e}
      .red{background:#fee2e2;color:#991b1b}.blue{background:#dbeafe;color:#1e40af}

      /* Table */
      table{border-collapse:collapse;width:100%;margin:8px 0}
      td,th{border:1px solid #cbd5e1;padding:6px 10px;text-align:left;font-size:13px}
      th{background:#f1f5f9;font-weight:600}

      .note{font-size:11px;color:#64748b;margin-top:10px}
      .space-y-4>*+*{margin-top:14px}
      .space-y-2>*+*{margin-top:8px}
      .mb-2{margin-bottom:8px}.mb-3{margin-bottom:12px}.mt-2{margin-top:8px}

      /* Hide buttons and SVG icons in print */
      button,.lucide{display:none!important}

      @media print{
        body{padding:16px 20px}
        .grid-cols-4{grid-template-columns:repeat(4,1fr)}
        .grid-cols-3{grid-template-columns:repeat(3,1fr)}
        .grid-cols-2{grid-template-columns:repeat(2,1fr)}
      }
    </style></head><body>`);
  w.document.write(el.innerHTML);
  w.document.write("</body></html>");
  w.document.close();
  setTimeout(() => w.print(), 200);
}

/* ================================================================
   INDIVIDUAL CALCULATORS
   ================================================================ */

/* ---------- 1. HbA1c ---------- */
function HbA1cCalc() {
  const [val, setVal] = useState(7.0);
  const hba1c = parseFloat(val) || 0;
  const mmol = Math.round((hba1c - 2.15) * 10.929);
  const avg = Math.round((hba1c * 1.5944 - 2.5944) * 10) / 10;
  let rating = "", cls = "blue";
  if (hba1c < 5.7) { rating = "Norm"; cls = "green"; }
  else if (hba1c < 6.5) { rating = "Prediabeet"; cls = "yellow"; }
  else if (hba1c < 7.5) { rating = "Diabeet — hea kontroll"; cls = "green"; }
  else if (hba1c < 9) { rating = "Vajab parandust"; cls = "yellow"; }
  else { rating = "Väga kõrge"; cls = "red"; }

  const TABLE = [
    [5.0,31,5.4,"Norm"],[5.5,37,6.2,"Norm"],[6.0,42,7.0,"Prediabeet"],
    [6.5,48,7.7,"Diabeet (eesmärk)"],[7.0,53,8.5,"Diabeet (eesmärk)"],
    [7.5,58,9.3,"Vajab parandust"],[8.0,64,10.1,"Vajab parandust"],
    [8.5,69,10.9,"Vajab parandust"],[9.0,75,11.7,"Väga kõrge"],
    [9.5,80,12.5,"Väga kõrge"],[10.0,86,13.3,"Väga kõrge"],
    [11.0,97,14.9,"Väga kõrge"],[12.0,108,16.5,"Väga kõrge"],
  ];

  return (
    <div>
      <label className="text-sm font-medium text-slate-700">Sisesta HbA1c väärtus (%)</label>
      <Input type="number" step="0.1" min="3" max="15" value={val} onChange={e => setVal(e.target.value)} className="mt-1 max-w-[200px]" data-testid="hba1c-input" />
      <div id="hba1c-result" className="mt-4 space-y-2">
        <h2 className="text-lg font-bold text-slate-900">HbA1c tulemus</h2>
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-xs text-slate-500">HbA1c</div><div className="text-xl font-bold">{hba1c.toFixed(1)} %</div></div>
          <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-xs text-slate-500">mmol/mol (IFCC)</div><div className="text-xl font-bold">{mmol}</div></div>
          <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-xs text-slate-500">Keskm. veresuhkur</div><div className="text-xl font-bold">{avg} mmol/L</div></div>
        </div>
        <div><span className={`badge ${cls}`}>{rating}</span></div>
        <table><thead><tr><th>HbA1c %</th><th>mmol/mol</th><th>Keskm. glükoos</th><th>Hinnang</th></tr></thead>
          <tbody>{TABLE.map(r => <tr key={r[0]}><td>{r[0]}</td><td>{r[1]}</td><td>{r[2]} mmol/L</td><td>{r[3]}</td></tr>)}</tbody></table>
        <p className="note">HbA1c eesmärk on enamasti &lt; 7%. Individuaalsed eesmärgid määrab arst.</p>
      </div>
      <Button size="sm" variant="outline" className="mt-3" onClick={() => printResult("hba1c-result")} data-testid="hba1c-print"><Printer className="w-4 h-4 mr-1" /> Prindi / Salvesta PDF</Button>
    </div>
  );
}

/* ---------- 2. KMI + Vööümbermõõt ---------- */
function BmiCalc() {
  const [h, setH] = useState(175);
  const [w, setW] = useState(78);
  const [waist, setWaist] = useState(90);
  const [sex, setSex] = useState("M");
  const hm = (parseFloat(h) || 170) / 100;
  const bmi = (parseFloat(w) || 70) / (hm * hm);
  let cat = "", cls = "blue";
  if (bmi < 18.5) { cat = "Alakaal"; cls = "yellow"; }
  else if (bmi < 25) { cat = "Normaalne"; cls = "green"; }
  else if (bmi < 30) { cat = "Ülekaal"; cls = "yellow"; }
  else { cat = "Rasvumine"; cls = "red"; }

  const wv = parseFloat(waist) || 0;
  let wrisk = "", wcls = "green";
  if (sex === "M") {
    if (wv >= 102) { wrisk = "Kõrge risk"; wcls = "red"; }
    else if (wv >= 94) { wrisk = "Mõõdukas risk"; wcls = "yellow"; }
    else { wrisk = "Madal risk"; }
  } else {
    if (wv >= 88) { wrisk = "Kõrge risk"; wcls = "red"; }
    else if (wv >= 80) { wrisk = "Mõõdukas risk"; wcls = "yellow"; }
    else { wrisk = "Madal risk"; }
  }

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2">
        <div><label className="text-xs text-slate-500">Pikkus (cm)</label><Input type="number" value={h} onChange={e => setH(e.target.value)} data-testid="bmi-height" /></div>
        <div><label className="text-xs text-slate-500">Kehakaal (kg)</label><Input type="number" value={w} onChange={e => setW(e.target.value)} data-testid="bmi-weight" /></div>
        <div><label className="text-xs text-slate-500">Vööümbermõõt (cm)</label><Input type="number" value={waist} onChange={e => setWaist(e.target.value)} data-testid="bmi-waist" /></div>
        <div><label className="text-xs text-slate-500">Sugu</label>
          <select className="w-full border rounded-md px-3 py-2 text-sm" value={sex} onChange={e => setSex(e.target.value)} data-testid="bmi-sex">
            <option value="M">Mees</option><option value="F">Naine</option>
          </select>
        </div>
      </div>
      <div id="bmi-result" className="mt-3 space-y-2">
        <h2 className="text-lg font-bold text-slate-900">KMI tulemus</h2>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-xs text-slate-500">KMI / BMI</div><div className="text-xl font-bold">{bmi.toFixed(1)}</div><span className={`badge ${cls}`}>{cat}</span></div>
          <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-xs text-slate-500">Vööümbermõõdu risk</div><div className="text-xl font-bold">{wv} cm</div><span className={`badge ${wcls}`}>{wrisk}</span></div>
        </div>
        <p className="note">KMI kategooriad: &lt;18.5 alakaal, 18.5–24.9 normaalne, 25–29.9 ülekaal, ≥30 rasvumine. Vööümbermõõt (WHO): Mees &lt;94/94–101/≥102cm, Naine &lt;80/80–87/≥88cm.</p>
      </div>
      <Button size="sm" variant="outline" className="mt-3" onClick={() => printResult("bmi-result")} data-testid="bmi-print"><Printer className="w-4 h-4 mr-1" /> Prindi / Salvesta PDF</Button>
    </div>
  );
}

/* ---------- 3. ICR ---------- */
function IcrCalc() {
  const [tdd, setTdd] = useState(40);
  const [carbs, setCarbs] = useState(60);
  const tddv = parseFloat(tdd) || 1;
  const icr = 500 / tddv;
  const bolus = (parseFloat(carbs) || 0) / icr;
  return (
    <div>
      <div className="grid grid-cols-2 gap-3 mb-2">
        <div><label className="text-xs text-slate-500">Päevane insuliini koguannus (TDD), TÜ</label><Input type="number" value={tdd} onChange={e => setTdd(e.target.value)} data-testid="icr-tdd" /></div>
        <div><label className="text-xs text-slate-500">Söögikorra süsivesikud (g)</label><Input type="number" value={carbs} onChange={e => setCarbs(e.target.value)} data-testid="icr-carbs" /></div>
      </div>
      <div id="icr-result" className="mt-3 space-y-2">
        <h2 className="text-lg font-bold text-slate-900">ICR tulemus</h2>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-xs text-slate-500">ICR</div><div className="text-xl font-bold">1 : {icr.toFixed(1)}</div><div className="text-xs text-slate-400">1 TÜ katab ~{icr.toFixed(1)} g süsivesikuid</div></div>
          <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-xs text-slate-500">Soovituslik boolus</div><div className="text-xl font-bold">{bolus.toFixed(1)} TÜ</div><div className="text-xs text-slate-400">{carbs} g ÷ {icr.toFixed(1)} g/TÜ</div></div>
        </div>
        <p className="note">Reegel 500 on lähtepunkt kiiretoimelisele insuliinile. Tegelik ICR võib varieeruda söögikordade lõikes. Kohanda ICR-i koos diabeediõe/endokrinoloogiga.</p>
      </div>
      <Button size="sm" variant="outline" className="mt-3" onClick={() => printResult("icr-result")} data-testid="icr-print"><Printer className="w-4 h-4 mr-1" /> Prindi / Salvesta PDF</Button>
    </div>
  );
}

/* ---------- 4. ISF ---------- */
function IsfCalc() {
  const [tdd, setTdd] = useState(40);
  const [cur, setCur] = useState(12);
  const [target, setTarget] = useState(6);
  const tddv = parseFloat(tdd) || 1;
  const isf = 100 / tddv;
  const correction = ((parseFloat(cur) || 0) - (parseFloat(target) || 0)) / isf;
  return (
    <div>
      <div className="grid grid-cols-3 gap-3 mb-2">
        <div><label className="text-xs text-slate-500">TDD (TÜ)</label><Input type="number" value={tdd} onChange={e => setTdd(e.target.value)} data-testid="isf-tdd" /></div>
        <div><label className="text-xs text-slate-500">Praegune veresuhkur (mmol/L)</label><Input type="number" step="0.1" value={cur} onChange={e => setCur(e.target.value)} data-testid="isf-current" /></div>
        <div><label className="text-xs text-slate-500">Sihtväärtus (mmol/L)</label><Input type="number" step="0.1" value={target} onChange={e => setTarget(e.target.value)} data-testid="isf-target" /></div>
      </div>
      <div id="isf-result" className="mt-3 space-y-2">
        <h2 className="text-lg font-bold text-slate-900">ISF tulemus</h2>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-xs text-slate-500">ISF</div><div className="text-xl font-bold">1 : {isf.toFixed(1)}</div><div className="text-xs text-slate-400">1 TÜ langetab ~{isf.toFixed(1)} mmol/L</div></div>
          <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-xs text-slate-500">Korrektsiooniannus</div><div className="text-xl font-bold">{Math.max(0, correction).toFixed(1)} TÜ</div><div className="text-xs text-slate-400">({cur} − {target}) ÷ {isf.toFixed(1)}</div></div>
        </div>
        <p className="note">Reegel 100 sobib kiiretoimelisele insuliinile. Ära stäkki korrektsiooniboolusi. Hüpoglükeemia korral (&lt; 4 mmol/L) ÄRA tee korrektsiooniboolust.</p>
      </div>
      <Button size="sm" variant="outline" className="mt-3" onClick={() => printResult("isf-result")} data-testid="isf-print"><Printer className="w-4 h-4 mr-1" /> Prindi / Salvesta PDF</Button>
    </div>
  );
}

/* ---------- 5. BMR ---------- */
function BmrCalc() {
  const [w, setW] = useState(75);
  const [h, setH] = useState(175);
  const [age, setAge] = useState(35);
  const [sex, setSex] = useState("M");
  const wv = parseFloat(w) || 70, hv = parseFloat(h) || 170, av = parseFloat(age) || 30;
  const bmr = sex === "M" ? 10 * wv + 6.25 * hv - 5 * av + 5 : 10 * wv + 6.25 * hv - 5 * av - 161;
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2">
        <div><label className="text-xs text-slate-500">Kehakaal (kg)</label><Input type="number" value={w} onChange={e => setW(e.target.value)} data-testid="bmr-weight" /></div>
        <div><label className="text-xs text-slate-500">Pikkus (cm)</label><Input type="number" value={h} onChange={e => setH(e.target.value)} data-testid="bmr-height" /></div>
        <div><label className="text-xs text-slate-500">Vanus</label><Input type="number" value={age} onChange={e => setAge(e.target.value)} data-testid="bmr-age" /></div>
        <div><label className="text-xs text-slate-500">Sugu</label>
          <select className="w-full border rounded-md px-3 py-2 text-sm" value={sex} onChange={e => setSex(e.target.value)} data-testid="bmr-sex"><option value="M">Mees</option><option value="F">Naine</option></select>
        </div>
      </div>
      <div id="bmr-result" className="mt-3">
        <h2 className="text-lg font-bold text-slate-900">BMR tulemus</h2>
        <div className="bg-slate-50 rounded-lg p-4 text-center mt-2"><div className="text-xs text-slate-500">Baasainevahetuskiirus (BMR)</div><div className="text-2xl font-bold">{Math.round(bmr)} kcal/päev</div></div>
        <p className="note mt-2">Mifflin-St Jeor valem. BMR on minimaalne energiakulu puhkeolekus.</p>
      </div>
      <Button size="sm" variant="outline" className="mt-3" onClick={() => printResult("bmr-result")}><Printer className="w-4 h-4 mr-1" /> Prindi / Salvesta PDF</Button>
    </div>
  );
}

/* ---------- 6. TDEE ---------- */
function TdeeCalc() {
  const [w, setW] = useState(75);
  const [h, setH] = useState(175);
  const [age, setAge] = useState(35);
  const [sex, setSex] = useState("M");
  const [act, setAct] = useState("1.55");
  const wv = parseFloat(w) || 70, hv = parseFloat(h) || 170, av = parseFloat(age) || 30;
  const bmr = sex === "M" ? 10 * wv + 6.25 * hv - 5 * av + 5 : 10 * wv + 6.25 * hv - 5 * av - 161;
  const tdee = bmr * parseFloat(act);
  const ACTS = [["1.2","Istuv (min liikumist)"],["1.375","Kerge (1–3x/näd)"],["1.55","Mõõdukas (3–5x/näd)"],["1.725","Aktiivne (6–7x/näd)"],["1.9","Väga aktiivne (2x/päevas)"]];
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2">
        <div><label className="text-xs text-slate-500">Kehakaal (kg)</label><Input type="number" value={w} onChange={e => setW(e.target.value)} /></div>
        <div><label className="text-xs text-slate-500">Pikkus (cm)</label><Input type="number" value={h} onChange={e => setH(e.target.value)} /></div>
        <div><label className="text-xs text-slate-500">Vanus</label><Input type="number" value={age} onChange={e => setAge(e.target.value)} /></div>
        <div><label className="text-xs text-slate-500">Sugu</label>
          <select className="w-full border rounded-md px-3 py-2 text-sm" value={sex} onChange={e => setSex(e.target.value)}><option value="M">Mees</option><option value="F">Naine</option></select>
        </div>
      </div>
      <div><label className="text-xs text-slate-500">Aktiivsustase</label>
        <select className="w-full border rounded-md px-3 py-2 text-sm mt-1" value={act} onChange={e => setAct(e.target.value)} data-testid="tdee-activity">
          {ACTS.map(a => <option key={a[0]} value={a[0]}>{a[1]}</option>)}
        </select>
      </div>
      <div id="tdee-result" className="mt-3">
        <h2 className="text-lg font-bold text-slate-900">TDEE tulemus</h2>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-xs text-slate-500">BMR</div><div className="text-xl font-bold">{Math.round(bmr)} kcal</div></div>
          <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-xs text-slate-500">TDEE</div><div className="text-xl font-bold">{Math.round(tdee)} kcal/päev</div></div>
        </div>
        <p className="note mt-2">TDEE = BMR × aktiivsuskoefitsient. Kaalu säilitamiseks söö TDEE jagu kaloreid.</p>
      </div>
      <Button size="sm" variant="outline" className="mt-3" onClick={() => printResult("tdee-result")}><Printer className="w-4 h-4 mr-1" /> Prindi / Salvesta PDF</Button>
    </div>
  );
}

/* ---------- 7. Kaloridefitsiit ---------- */
function DeficitCalc() {
  const [tdee, setTdee] = useState(2200);
  const [goal, setGoal] = useState("500");
  const tdeev = parseFloat(tdee) || 2000;
  const deficit = parseFloat(goal) || 500;
  const target = tdeev - deficit;
  const weeklyLoss = (deficit * 7 / 7700).toFixed(2);
  return (
    <div>
      <div className="grid grid-cols-2 gap-3 mb-2">
        <div><label className="text-xs text-slate-500">Sinu TDEE (kcal/päev)</label><Input type="number" value={tdee} onChange={e => setTdee(e.target.value)} data-testid="deficit-tdee" /></div>
        <div><label className="text-xs text-slate-500">Defitsiit</label>
          <select className="w-full border rounded-md px-3 py-2 text-sm" value={goal} onChange={e => setGoal(e.target.value)} data-testid="deficit-goal">
            <option value="250">Kerge (−250 kcal)</option><option value="500">Mõõdukas (−500 kcal)</option><option value="750">Agressiivne (−750 kcal)</option><option value="1000">Väga agressiivne (−1000 kcal)</option>
          </select>
        </div>
      </div>
      <div id="deficit-result" className="mt-3">
        <h2 className="text-lg font-bold text-slate-900">Kaloridefitsiidi tulemus</h2>
        <div className="grid grid-cols-3 gap-3 mt-2">
          <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-xs text-slate-500">Sihtkalorid</div><div className="text-xl font-bold">{Math.round(target)} kcal</div></div>
          <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-xs text-slate-500">Defitsiit</div><div className="text-xl font-bold">−{deficit} kcal</div></div>
          <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-xs text-slate-500">Kaalulangus/näd</div><div className="text-xl font-bold">~{weeklyLoss} kg</div></div>
        </div>
        <p className="note mt-2">Ohutu kaalulangus on 0.5–1 kg nädalas. Liiga suur defitsiit võib alandada ainevahetuskiirust.</p>
      </div>
      <Button size="sm" variant="outline" className="mt-3" onClick={() => printResult("deficit-result")}><Printer className="w-4 h-4 mr-1" /> Prindi / Salvesta PDF</Button>
    </div>
  );
}

/* ---------- 8. Valgu kalkulaator ---------- */
function ProteinCalc() {
  const [w, setW] = useState(75);
  const [goal, setGoal] = useState("1.6");
  const wv = parseFloat(w) || 70;
  const mult = parseFloat(goal) || 1.6;
  const daily = wv * mult;
  const perMeal = daily / 4;
  const GOALS = [["0.8","Istuv eluviis"],["1.2","Aktiivne / kaalu säilitamine"],["1.6","Lihasmassi kasvatamine"],["2.0","Intensiivne treening"],["2.2","Maksimaalne lihasmassi kasv"]];
  return (
    <div>
      <div className="grid grid-cols-2 gap-3 mb-2">
        <div><label className="text-xs text-slate-500">Kehakaal (kg)</label><Input type="number" value={w} onChange={e => setW(e.target.value)} data-testid="protein-weight" /></div>
        <div><label className="text-xs text-slate-500">Eesmärk</label>
          <select className="w-full border rounded-md px-3 py-2 text-sm" value={goal} onChange={e => setGoal(e.target.value)} data-testid="protein-goal">
            {GOALS.map(g => <option key={g[0]} value={g[0]}>{g[1]} ({g[0]} g/kg)</option>)}
          </select>
        </div>
      </div>
      <div id="protein-result" className="mt-3">
        <h2 className="text-lg font-bold text-slate-900">Valguvajadus</h2>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-xs text-slate-500">Päevane valk</div><div className="text-xl font-bold">{Math.round(daily)} g</div></div>
          <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-xs text-slate-500">Söögikorra kohta (4x)</div><div className="text-xl font-bold">{Math.round(perMeal)} g</div></div>
        </div>
      </div>
      <Button size="sm" variant="outline" className="mt-3" onClick={() => printResult("protein-result")}><Printer className="w-4 h-4 mr-1" /> Prindi / Salvesta PDF</Button>
    </div>
  );
}

/* ---------- 9. Pulsitsoonid ---------- */
function HeartRateCalc() {
  const [age, setAge] = useState(35);
  const [rest, setRest] = useState(65);
  const maxHr = 220 - (parseFloat(age) || 30);
  const rv = parseFloat(rest) || 60;
  const zone = (lo, hi) => [Math.round((maxHr - rv) * lo + rv), Math.round((maxHr - rv) * hi + rv)];
  const ZONES = [
    ["Tsoon 1 — Taastumine", 0.5, 0.6, "green"],
    ["Tsoon 2 — Rasvapõletus", 0.6, 0.7, "blue"],
    ["Tsoon 3 — Aeroobne", 0.7, 0.8, "yellow"],
    ["Tsoon 4 — Anaeroobne", 0.8, 0.9, "yellow"],
    ["Tsoon 5 — Maksimum", 0.9, 1.0, "red"],
  ];
  return (
    <div>
      <div className="grid grid-cols-2 gap-3 mb-2">
        <div><label className="text-xs text-slate-500">Vanus</label><Input type="number" value={age} onChange={e => setAge(e.target.value)} data-testid="hr-age" /></div>
        <div><label className="text-xs text-slate-500">Puhkepulss (lööki/min)</label><Input type="number" value={rest} onChange={e => setRest(e.target.value)} data-testid="hr-rest" /></div>
      </div>
      <div id="hr-result" className="mt-3">
        <h2 className="text-lg font-bold text-slate-900">Pulsitsoonid (Karvonen)</h2>
        <div className="text-sm text-slate-500 mb-2">Max pulss: {maxHr} lööki/min</div>
        <table><thead><tr><th>Tsoon</th><th>Pulss (lööki/min)</th></tr></thead>
          <tbody>{ZONES.map(z => { const [lo, hi] = zone(z[1], z[2]); return <tr key={z[0]}><td>{z[0]}</td><td><span className={`badge ${z[3]}`}>{lo}–{hi}</span></td></tr>; })}</tbody></table>
      </div>
      <Button size="sm" variant="outline" className="mt-3" onClick={() => printResult("hr-result")}><Printer className="w-4 h-4 mr-1" /> Prindi / Salvesta PDF</Button>
    </div>
  );
}

/* ---------- 10. Jooksutempo ---------- REMOVED */

/* ---------- 11. Kaloripõletus ---------- */
function CalorieBurnCalc() {
  const [w, setW] = useState(75);
  const [dur, setDur] = useState(30);
  const [act, setAct] = useState("7");
  const wv = parseFloat(w) || 70;
  const hours = (parseFloat(dur) || 30) / 60;
  const met = parseFloat(act) || 7;
  const burned = met * wv * hours;
  const ACTS = [["3.5","Kõndimine (5 km/h)"],["6","Jalgrattasõit (kerge)"],["7","Jooksmine (8 km/h)"],["8","Jooksmine (9.5 km/h)"],["10","Jooksmine (12 km/h)"],["8","Ujumine (mõõdukas)"],["9","HIIT / Intervallid"],["5","Jõutreening"],["4","Jooga"],["6","Tantsimine"]];
  return (
    <div>
      <div className="grid grid-cols-3 gap-3 mb-2">
        <div><label className="text-xs text-slate-500">Kehakaal (kg)</label><Input type="number" value={w} onChange={e => setW(e.target.value)} /></div>
        <div><label className="text-xs text-slate-500">Kestus (min)</label><Input type="number" value={dur} onChange={e => setDur(e.target.value)} /></div>
        <div><label className="text-xs text-slate-500">Tegevus</label>
          <select className="w-full border rounded-md px-3 py-2 text-sm" value={act} onChange={e => setAct(e.target.value)} data-testid="burn-activity">
            {ACTS.map(a => <option key={a[0]+a[1]} value={a[0]}>{a[1]} (MET {a[0]})</option>)}
          </select>
        </div>
      </div>
      <div id="burn-result" className="mt-3">
        <h2 className="text-lg font-bold text-slate-900">Kaloripõletus</h2>
        <div className="bg-slate-50 rounded-lg p-4 text-center mt-2"><div className="text-xs text-slate-500">Hinnanguline kaloripõletus</div><div className="text-2xl font-bold">{Math.round(burned)} kcal</div><div className="text-xs text-slate-400">MET {met} × {wv} kg × {hours.toFixed(2)} h</div></div>
      </div>
      <Button size="sm" variant="outline" className="mt-3" onClick={() => printResult("burn-result")}><Printer className="w-4 h-4 mr-1" /> Prindi / Salvesta PDF</Button>
    </div>
  );
}

/* ---------- 12. Keharasvaprotsent (Navy) ---------- */
function BodyFatCalc() {
  const [sex, setSex] = useState("M");
  const [h, setH] = useState(175);
  const [waist, setWaist] = useState(85);
  const [neck, setNeck] = useState(38);
  const [hip, setHip] = useState(95);
  const hv = parseFloat(h) || 175, wv = parseFloat(waist) || 85, nv = parseFloat(neck) || 38, hipv = parseFloat(hip) || 95;
  let bf;
  if (sex === "M") {
    bf = 495 / (1.0324 - 0.19077 * Math.log10(wv - nv) + 0.15456 * Math.log10(hv)) - 450;
  } else {
    bf = 495 / (1.29579 - 0.35004 * Math.log10(wv + hipv - nv) + 0.22100 * Math.log10(hv)) - 450;
  }
  bf = Math.max(2, Math.min(60, bf));
  let cat = "", cls = "blue";
  if (sex === "M") {
    if (bf < 6) { cat = "Oluline rasv"; cls = "yellow"; } else if (bf < 14) { cat = "Sportlik"; cls = "green"; } else if (bf < 18) { cat = "Hea vorm"; cls = "green"; } else if (bf < 25) { cat = "Keskmine"; cls = "yellow"; } else { cat = "Ülekaal"; cls = "red"; }
  } else {
    if (bf < 14) { cat = "Oluline rasv"; cls = "yellow"; } else if (bf < 21) { cat = "Sportlik"; cls = "green"; } else if (bf < 25) { cat = "Hea vorm"; cls = "green"; } else if (bf < 32) { cat = "Keskmine"; cls = "yellow"; } else { cat = "Ülekaal"; cls = "red"; }
  }
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-2">
        <div><label className="text-xs text-slate-500">Sugu</label><select className="w-full border rounded-md px-3 py-2 text-sm" value={sex} onChange={e => setSex(e.target.value)}><option value="M">Mees</option><option value="F">Naine</option></select></div>
        <div><label className="text-xs text-slate-500">Pikkus (cm)</label><Input type="number" value={h} onChange={e => setH(e.target.value)} /></div>
        <div><label className="text-xs text-slate-500">Vööümbermõõt (cm)</label><Input type="number" value={waist} onChange={e => setWaist(e.target.value)} /></div>
        <div><label className="text-xs text-slate-500">Kaela ümbermõõt (cm)</label><Input type="number" value={neck} onChange={e => setNeck(e.target.value)} /></div>
        {sex === "F" && <div><label className="text-xs text-slate-500">Puusa ümbermõõt (cm)</label><Input type="number" value={hip} onChange={e => setHip(e.target.value)} /></div>}
      </div>
      <div id="bf-result" className="mt-3">
        <h2 className="text-lg font-bold text-slate-900">Keharasvaprotsent (US Navy)</h2>
        <div className="bg-slate-50 rounded-lg p-4 text-center mt-2"><div className="text-xs text-slate-500">Keharasvaprotsent</div><div className="text-2xl font-bold">{bf.toFixed(1)} %</div><span className={`badge ${cls}`}>{cat}</span></div>
      </div>
      <Button size="sm" variant="outline" className="mt-3" onClick={() => printResult("bf-result")}><Printer className="w-4 h-4 mr-1" /> Prindi / Salvesta PDF</Button>
    </div>
  );
}

/* ---------- 13. Ideaalkaal (Devine) ---------- */
function IdealWeightCalc() {
  const [h, setH] = useState(175);
  const [sex, setSex] = useState("M");
  const inches = (parseFloat(h) || 170) / 2.54;
  const ideal = sex === "M" ? 50 + 2.3 * (inches - 60) : 45.5 + 2.3 * (inches - 60);
  return (
    <div>
      <div className="grid grid-cols-2 gap-3 mb-2">
        <div><label className="text-xs text-slate-500">Pikkus (cm)</label><Input type="number" value={h} onChange={e => setH(e.target.value)} /></div>
        <div><label className="text-xs text-slate-500">Sugu</label><select className="w-full border rounded-md px-3 py-2 text-sm" value={sex} onChange={e => setSex(e.target.value)}><option value="M">Mees</option><option value="F">Naine</option></select></div>
      </div>
      <div id="ideal-result" className="mt-3">
        <h2 className="text-lg font-bold text-slate-900">Ideaalkaal (Devine)</h2>
        <div className="bg-slate-50 rounded-lg p-4 text-center mt-2"><div className="text-xs text-slate-500">Ideaalkaal</div><div className="text-2xl font-bold">{ideal.toFixed(1)} kg</div><div className="text-xs text-slate-400">Tervislik vahemik: {(ideal * 0.9).toFixed(0)}–{(ideal * 1.1).toFixed(0)} kg</div></div>
      </div>
      <Button size="sm" variant="outline" className="mt-3" onClick={() => printResult("ideal-result")}><Printer className="w-4 h-4 mr-1" /> Prindi / Salvesta PDF</Button>
    </div>
  );
}

/* ---------- 14. Makrod ---------- */
function MacroCalc() {
  const [kcal, setKcal] = useState(2200);
  const [split, setSplit] = useState("40/30/30");
  const kv = parseFloat(kcal) || 2000;
  const [cp, fp, pp] = split.split("/").map(Number);
  const carbG = Math.round(kv * (cp / 100) / 4);
  const fatG = Math.round(kv * (fp / 100) / 9);
  const protG = Math.round(kv * (pp / 100) / 4);
  const SPLITS = ["40/30/30","50/25/25","30/35/35","40/20/40"];
  return (
    <div>
      <div className="grid grid-cols-2 gap-3 mb-2">
        <div><label className="text-xs text-slate-500">Päevakaloraaž (kcal)</label><Input type="number" value={kcal} onChange={e => setKcal(e.target.value)} /></div>
        <div><label className="text-xs text-slate-500">Jaotus (SV/Rasv/Valk %)</label>
          <select className="w-full border rounded-md px-3 py-2 text-sm" value={split} onChange={e => setSplit(e.target.value)}>
            {SPLITS.map(s => <option key={s} value={s}>{s.replace(/\//g," / ")}</option>)}
          </select>
        </div>
      </div>
      <div id="macro-result" className="mt-3">
        <h2 className="text-lg font-bold text-slate-900">Makrojaotus</h2>
        <div className="grid grid-cols-3 gap-3 mt-2">
          <div className="bg-amber-50 rounded-lg p-3 text-center"><div className="text-xs text-amber-600">Süsivesikud ({cp}%)</div><div className="text-xl font-bold">{carbG} g</div></div>
          <div className="bg-rose-50 rounded-lg p-3 text-center"><div className="text-xs text-rose-600">Rasvad ({fp}%)</div><div className="text-xl font-bold">{fatG} g</div></div>
          <div className="bg-blue-50 rounded-lg p-3 text-center"><div className="text-xs text-blue-600">Valgud ({pp}%)</div><div className="text-xl font-bold">{protG} g</div></div>
        </div>
      </div>
      <Button size="sm" variant="outline" className="mt-3" onClick={() => printResult("macro-result")}><Printer className="w-4 h-4 mr-1" /> Prindi / Salvesta PDF</Button>
    </div>
  );
}

/* ---------- 15. Vedelik + 16. 1RM ---------- REMOVED */

/* ================================================================
   17. KOGU ANALÜÜS (Complete Analysis)
   ================================================================ */
function FullAnalysisCalc() {
  const [w, setW] = useState(75);
  const [h, setH] = useState(175);
  const [age, setAge] = useState(35);
  const [sex, setSex] = useState("M");
  const [act, setAct] = useState("1.55");
  const [waist, setWaist] = useState(85);
  const [neck, setNeck] = useState(38);
  const [hip, setHip] = useState(95);
  const [rest, setRest] = useState(65);

  const wv = parseFloat(w) || 70, hv = parseFloat(h) || 170, av = parseFloat(age) || 30;
  const hm = hv / 100;

  // BMI
  const bmi = wv / (hm * hm);
  let bmiCat = "", bmiCls = "blue";
  if (bmi < 18.5) { bmiCat = "Alakaal"; bmiCls = "yellow"; }
  else if (bmi < 25) { bmiCat = "Normaalne"; bmiCls = "green"; }
  else if (bmi < 30) { bmiCat = "Ülekaal"; bmiCls = "yellow"; }
  else { bmiCat = "Rasvumine"; bmiCls = "red"; }

  // BMR + TDEE
  const bmr = sex === "M" ? 10 * wv + 6.25 * hv - 5 * av + 5 : 10 * wv + 6.25 * hv - 5 * av - 161;
  const tdee = bmr * parseFloat(act);

  // Body fat (Navy)
  const wstv = parseFloat(waist) || 85, nkv = parseFloat(neck) || 38, hipv = parseFloat(hip) || 95;
  let bf;
  if (sex === "M") {
    bf = 495 / (1.0324 - 0.19077 * Math.log10(Math.max(1, wstv - nkv)) + 0.15456 * Math.log10(hv)) - 450;
  } else {
    bf = 495 / (1.29579 - 0.35004 * Math.log10(Math.max(1, wstv + hipv - nkv)) + 0.22100 * Math.log10(hv)) - 450;
  }
  bf = Math.max(2, Math.min(60, bf));
  let bfCat = "", bfCls = "blue";
  if (sex === "M") {
    if (bf < 14) { bfCat = "Sportlik"; bfCls = "green"; } else if (bf < 18) { bfCat = "Hea vorm"; bfCls = "green"; } else if (bf < 25) { bfCat = "Keskmine"; bfCls = "yellow"; } else { bfCat = "Ülekaal"; bfCls = "red"; }
  } else {
    if (bf < 21) { bfCat = "Sportlik"; bfCls = "green"; } else if (bf < 25) { bfCat = "Hea vorm"; bfCls = "green"; } else if (bf < 32) { bfCat = "Keskmine"; bfCls = "yellow"; } else { bfCat = "Ülekaal"; bfCls = "red"; }
  }
  const leanMass = wv * (1 - bf / 100);

  // Ideal weight (Devine)
  const inches = hv / 2.54;
  const ideal = sex === "M" ? 50 + 2.3 * (inches - 60) : 45.5 + 2.3 * (inches - 60);

  // Macros (40/30/30)
  const carbG = Math.round(tdee * 0.4 / 4);
  const fatG = Math.round(tdee * 0.3 / 9);
  const protG = Math.round(tdee * 0.3 / 4);

  // Heart rate zones
  const maxHr = 220 - av;
  const rv = parseFloat(rest) || 60;
  const zone = (lo, hi) => [Math.round((maxHr - rv) * lo + rv), Math.round((maxHr - rv) * hi + rv)];
  const ZONES = [
    ["Tsoon 1 — Taastumine", 0.5, 0.6, "green"],
    ["Tsoon 2 — Rasvapõletus", 0.6, 0.7, "blue"],
    ["Tsoon 3 — Aeroobne", 0.7, 0.8, "yellow"],
    ["Tsoon 4 — Anaeroobne", 0.8, 0.9, "yellow"],
    ["Tsoon 5 — Maksimum", 0.9, 1.0, "red"],
  ];

  // Waist risk
  let wrisk = "", wcls = "green";
  if (sex === "M") {
    if (wstv >= 102) { wrisk = "Kõrge risk"; wcls = "red"; } else if (wstv >= 94) { wrisk = "Mõõdukas"; wcls = "yellow"; } else { wrisk = "Madal"; }
  } else {
    if (wstv >= 88) { wrisk = "Kõrge risk"; wcls = "red"; } else if (wstv >= 80) { wrisk = "Mõõdukas"; wcls = "yellow"; } else { wrisk = "Madal"; }
  }

  const ACTS = [["1.2","Istuv"],["1.375","Kerge (1–3x/näd)"],["1.55","Mõõdukas (3–5x/näd)"],["1.725","Aktiivne (6–7x/näd)"],["1.9","Väga aktiivne"]];

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
        <div><label className="text-xs text-slate-500">Kehakaal (kg)</label><Input type="number" value={w} onChange={e => setW(e.target.value)} data-testid="full-weight" /></div>
        <div><label className="text-xs text-slate-500">Pikkus (cm)</label><Input type="number" value={h} onChange={e => setH(e.target.value)} data-testid="full-height" /></div>
        <div><label className="text-xs text-slate-500">Vanus</label><Input type="number" value={age} onChange={e => setAge(e.target.value)} data-testid="full-age" /></div>
        <div><label className="text-xs text-slate-500">Sugu</label>
          <select className="w-full border rounded-md px-3 py-2 text-sm" value={sex} onChange={e => setSex(e.target.value)} data-testid="full-sex"><option value="M">Mees</option><option value="F">Naine</option></select>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
        <div><label className="text-xs text-slate-500">Vööümbermõõt (cm)</label><Input type="number" value={waist} onChange={e => setWaist(e.target.value)} /></div>
        <div><label className="text-xs text-slate-500">Kaela ümbermõõt (cm)</label><Input type="number" value={neck} onChange={e => setNeck(e.target.value)} /></div>
        {sex === "F" && <div><label className="text-xs text-slate-500">Puusa ümbermõõt (cm)</label><Input type="number" value={hip} onChange={e => setHip(e.target.value)} /></div>}
        <div><label className="text-xs text-slate-500">Puhkepulss (lööki/min)</label><Input type="number" value={rest} onChange={e => setRest(e.target.value)} /></div>
      </div>
      <div className="mb-3">
        <label className="text-xs text-slate-500">Aktiivsustase</label>
        <select className="w-full border rounded-md px-3 py-2 text-sm mt-1" value={act} onChange={e => setAct(e.target.value)} data-testid="full-activity">
          {ACTS.map(a => <option key={a[0]} value={a[0]}>{a[1]}</option>)}
        </select>
      </div>

      <div id="full-result" className="space-y-4">
        <h2 className="text-lg font-bold text-slate-900">Kogu analüüs — tulemused</h2>

        {/* Row 1: BMI, Body Fat, Ideal Weight */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-xs text-slate-500">KMI</div><div className="text-xl font-bold">{bmi.toFixed(1)}</div><span className={`badge ${bmiCls}`}>{bmiCat}</span></div>
          <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-xs text-slate-500">Keharasv</div><div className="text-xl font-bold">{bf.toFixed(1)}%</div><span className={`badge ${bfCls}`}>{bfCat}</span></div>
          <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-xs text-slate-500">Rasvavaba mass</div><div className="text-xl font-bold">{leanMass.toFixed(1)} kg</div></div>
          <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-xs text-slate-500">Ideaalkaal</div><div className="text-xl font-bold">{ideal.toFixed(1)} kg</div></div>
        </div>

        {/* Row 2: BMR, TDEE, Waist */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-xs text-slate-500">BMR</div><div className="text-xl font-bold">{Math.round(bmr)} kcal</div></div>
          <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-xs text-slate-500">TDEE</div><div className="text-xl font-bold">{Math.round(tdee)} kcal</div></div>
          <div className="bg-slate-50 rounded-lg p-3 text-center"><div className="text-xs text-slate-500">Vööümbermõõdu risk</div><div className="text-xl font-bold">{wstv} cm</div><span className={`badge ${wcls}`}>{wrisk}</span></div>
        </div>

        {/* Row 3: Macros */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-amber-50 rounded-lg p-3 text-center"><div className="text-xs text-amber-600">Süsivesikud (40%)</div><div className="text-xl font-bold">{carbG} g</div></div>
          <div className="bg-rose-50 rounded-lg p-3 text-center"><div className="text-xs text-rose-600">Rasvad (30%)</div><div className="text-xl font-bold">{fatG} g</div></div>
          <div className="bg-blue-50 rounded-lg p-3 text-center"><div className="text-xs text-blue-600">Valgud (30%)</div><div className="text-xl font-bold">{protG} g</div></div>
        </div>

        {/* Row 4: Calorie targets */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-green-50 rounded-lg p-3 text-center"><div className="text-xs text-green-600">Kaalu säilitamine</div><div className="text-xl font-bold">{Math.round(tdee)} kcal</div></div>
          <div className="bg-orange-50 rounded-lg p-3 text-center"><div className="text-xs text-orange-600">Kaalulangus (−500)</div><div className="text-xl font-bold">{Math.round(tdee - 500)} kcal</div></div>
          <div className="bg-purple-50 rounded-lg p-3 text-center"><div className="text-xs text-purple-600">Massi kasvatamine (+300)</div><div className="text-xl font-bold">{Math.round(tdee + 300)} kcal</div></div>
        </div>

        {/* Heart Rate Zones */}
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Pulsitsoonid (max: {maxHr} lööki/min)</h3>
          <table><thead><tr><th>Tsoon</th><th>Pulss</th></tr></thead>
            <tbody>{ZONES.map(z => { const [lo, hi] = zone(z[1], z[2]); return <tr key={z[0]}><td>{z[0]}</td><td><span className={`badge ${z[3]}`}>{lo}–{hi}</span></td></tr>; })}</tbody></table>
        </div>
      </div>
      <Button size="sm" variant="outline" className="mt-3" onClick={() => printResult("full-result")} data-testid="full-print"><Printer className="w-4 h-4 mr-1" /> Prindi / Salvesta PDF</Button>
    </div>
  );
}

/* ---------- 17. Rasvavaba mass ---------- */
function LeanMassCalc() {
  const [w, setW] = useState(80);
  const [bf, setBf] = useState(20);
  const wv = parseFloat(w) || 70;
  const bfv = parseFloat(bf) || 20;
  const lean = wv * (1 - bfv / 100);
  const fatMass = wv - lean;
  return (
    <div>
      <div className="grid grid-cols-2 gap-3 mb-2">
        <div><label className="text-xs text-slate-500">Kehakaal (kg)</label><Input type="number" value={w} onChange={e => setW(e.target.value)} /></div>
        <div><label className="text-xs text-slate-500">Keharasvaprotsent (%)</label><Input type="number" value={bf} onChange={e => setBf(e.target.value)} /></div>
      </div>
      <div id="lean-result" className="mt-3">
        <h2 className="text-lg font-bold text-slate-900">Rasvavaba mass</h2>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div className="bg-green-50 rounded-lg p-3 text-center"><div className="text-xs text-green-600">Rasvavaba mass</div><div className="text-xl font-bold">{lean.toFixed(1)} kg</div></div>
          <div className="bg-orange-50 rounded-lg p-3 text-center"><div className="text-xs text-orange-600">Rasvmass</div><div className="text-xl font-bold">{fatMass.toFixed(1)} kg</div></div>
        </div>
      </div>
      <Button size="sm" variant="outline" className="mt-3" onClick={() => printResult("lean-result")}><Printer className="w-4 h-4 mr-1" /> Prindi / Salvesta PDF</Button>
    </div>
  );
}

/* ================================================================
   MAIN PAGE
   ================================================================ */
const MEDICAL = [
  { id: "full", name: "Kogu analüüs", desc: "KMI, BMR, TDEE, keharasv, makrod, pulsitsoonid — kõik korraga", icon: Activity, component: FullAnalysisCalc },
  { id: "hba1c", name: "HbA1c kalkulaator", desc: "HbA1c %, mmol/mol ja keskmine veresuhkur", icon: Beaker, component: HbA1cCalc },
  { id: "bmi", name: "KMI + Vööümbermõõt", desc: "Kehamassiindeks ja kõhupiirkonna risk", icon: Scale, component: BmiCalc },
  { id: "icr", name: "ICR — insuliini-SV suhe", desc: "Insuliini-süsivesikute suhe (reegel 500)", icon: Syringe, component: IcrCalc },
  { id: "isf", name: "ISF — insuliini tundlikkus", desc: "Insuliini tundlikkuse tegur (reegel 100)", icon: Droplets, component: IsfCalc },
];

const FITNESS = [
  { id: "bmr", name: "BMR — baasainevahetuskiirus", desc: "Minimaalne energiakulu puhkeolekus", icon: Flame, component: BmrCalc },
  { id: "tdee", name: "TDEE — päevane energiakulu", desc: "Kalorite vajadus vastavalt aktiivsusele", icon: Activity, component: TdeeCalc },
  { id: "deficit", name: "Kaloridefitsiit", desc: "Sihtkalorid kaalulanguseks", icon: Target, component: DeficitCalc },
  { id: "protein", name: "Valgu kalkulaator", desc: "Päevane valguvajadus eesmärgi järgi", icon: Utensils, component: ProteinCalc },
  { id: "macro", name: "Makrojaotus", desc: "Süsivesikud, rasvad, valgud grammides", icon: Utensils, component: MacroCalc },
  { id: "heartrate", name: "Pulsitsoonid", desc: "5 treeningutsooni (Karvonen)", icon: Heart, component: HeartRateCalc },
  { id: "burn", name: "Kaloripõletus", desc: "Kalorid tegevuse, kaalu ja kestuse järgi", icon: Flame, component: CalorieBurnCalc },
  { id: "bodyfat", name: "Keharasvaprotsent", desc: "US Navy mõõtmismeetod", icon: Scale, component: BodyFatCalc },
  { id: "leanmass", name: "Rasvavaba mass", desc: "Lihasmass vs rasvmass", icon: Dumbbell, component: LeanMassCalc },
  { id: "idealweight", name: "Ideaalkaal", desc: "Devine valem pikkuse järgi", icon: Target, component: IdealWeightCalc },
];

export default function CalculatorsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [active, setActive] = useState(null);

  const toggle = (id) => setActive(active === id ? null : id);

  const renderSection = (title, items) => (
    <div className="mb-6">
      <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">{title}</h3>
      <div className="space-y-2">
        {items.map(calc => {
          const Icon = calc.icon;
          const isOpen = active === calc.id;
          return (
            <div key={calc.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`calc-${calc.id}`}>
              <button
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors text-left"
                onClick={() => toggle(calc.id)}
                data-testid={`calc-toggle-${calc.id}`}
              >
                <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900">{calc.name}</div>
                  <div className="text-xs text-slate-400">{calc.desc}</div>
                </div>
                {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </button>
              {isOpen && (
                <div className="px-4 pb-4 border-t border-slate-100 pt-3">
                  <calc.component />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="md:ml-64">
        <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
          <SidebarMenuButton onClick={() => setSidebarOpen(true)} />
          <Calculator className="w-5 h-5 text-slate-700" />
          <h1 className="text-lg font-bold text-slate-900" data-testid="calculators-title">Kalkulaatorid</h1>
          <div className="flex-1" />
          <Button variant="ghost" size="sm" onClick={() => navigate("/")} data-testid="calc-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Pealeht
          </Button>
        </header>
        <div className="max-w-3xl mx-auto p-4">
          {renderSection("Meditsiinilised kalkulaatorid", MEDICAL)}
          {renderSection("Treeningu ja toitumise kalkulaatorid", FITNESS)}
        </div>
      </div>
    </div>
  );
}
